import { useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Check, Info, Plus, Scale } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import {
  Button,
  Card,
  EmptyState,
  Field,
  DurationInput,
  NumberStepper,
  PageLoader,
  SectionTitle,
  Select,
  TextField,
  Toggle,
  cx,
} from "@/components/ui";
import { IconPicker, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { useAction, usePenalties, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { formatUnits, isConvertible } from "@shared/economy";
import { formatDuration } from "@shared/duration";
import { PENALTY_REASONS } from "@shared/templates";
import type { AdviceResponse, FamilyDTO, PenaltyIdeaDTO, PenaltyPresetDTO, RewardTypeDTO } from "@shared/types";
import { AdvisorCard, AdvisorSource, AdvisorThinking, ConfirmDelete, currencyWord, useAdvice } from "./Rewards";

const PENALTY_ICONS = ["⚠️", "🛑", "🙊", "🤥", "😤", "🥊", "📵", "🎮", "📺", "🛌", "⏰", "🗣️", "🚫", "🧹", "👂", "💢"];

// ---------------------------------------------------------------------------------------------
// Balance rules (family settings)
// ---------------------------------------------------------------------------------------------

function BalanceRules({ family }: { family: FamilyDTO }) {
  const initial = {
    allowNegative: family.allowNegative,
    floor: Math.abs(family.negativeFloor),
    workItOffEnabled: family.workItOffEnabled,
  };
  const [rules, setRules] = useState(initial);
  const set = (patch: Partial<typeof rules>) => setRules({ ...rules, ...patch });
  const dirty =
    rules.allowNegative !== initial.allowNegative ||
    rules.floor !== initial.floor ||
    rules.workItOffEnabled !== initial.workItOffEnabled;

  const save = useAction(
    () =>
      api.patch("/family", {
        allowNegative: rules.allowNegative,
        negativeFloor: -Math.round(rules.floor),
        workItOffEnabled: rules.workItOffEnabled,
      }),
    { success: "Rules saved" },
  );

  const currency = family.currencyName;
  return (
    <Card className="space-y-4">
      <Toggle
        checked={rules.allowNegative}
        onChange={(allowNegative) => set({ allowNegative, floor: allowNegative && rules.floor === 0 ? 50 : rules.floor })}
        label="Allow balances below zero"
        description={`Kids can owe ${currency} after a deduction, then earn their way back.`}
      />

      {rules.allowNegative && (
        <Field label="How far below zero?" hint={rules.floor === 0 ? "At 0, balances still stop at zero." : undefined}>
          <NumberStepper value={rules.floor} onChange={(floor) => set({ floor: Math.round(floor) })} min={0} max={1000} step={10} suffix={family.currencyIcon} />
          <p className="pt-1 text-sm font-bold">
            Lowest balance: <Points value={-rules.floor} family={family} className="text-danger" />
          </p>
        </Field>
      )}

      <div className="flex gap-2 rounded-2xl bg-sunken px-3 py-2.5 text-sm text-muted">
        <Info className="mt-0.5 size-4 shrink-0" />
        <p>
          Deductions draw from earned-but-unspent rewards.{" "}
          {rules.allowNegative && rules.floor > 0
            ? `They can take a balance down to −${rules.floor.toLocaleString()} ${currency}, then stop.`
            : "With a hard floor at zero, deductions stop at zero — kids never owe."}
        </p>
      </div>

      <div className="border-t border-line" />

      <Toggle
        checked={rules.workItOffEnabled}
        onChange={(workItOffEnabled) => set({ workItOffEnabled })}
        label="Work it off"
        description="Kids below zero see special jobs that pay back what they owe — only up to zero, never extra."
      />
      {rules.workItOffEnabled && !rules.allowNegative && (
        <p className="rounded-2xl bg-warn-soft px-3 py-2 text-sm font-semibold text-warn">
          Balances can't go below zero, so work-it-off jobs won't appear until you allow it above.
        </p>
      )}
      {rules.workItOffEnabled && (
        <Link to="/parent/plan/jobs" className="inline-flex items-center gap-1 text-sm font-bold text-brand">
          Set up work-it-off jobs <ArrowRight className="size-4" />
        </Link>
      )}

      {dirty && (
        <div className="flex gap-2 pt-1">
          <Button variant="secondary" onClick={() => setRules(initial)} className="shrink-0">
            Undo
          </Button>
          <Button block onClick={() => save.mutate()} loading={save.isPending}>
            Save rules
          </Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Penalty presets
// ---------------------------------------------------------------------------------------------

type Draft = Omit<PenaltyPresetDTO, "id"> & { id?: string };

const empty: Draft = { name: "", icon: "⚠️", amount: 10, rewardTypeId: null };

/** Stepper settings for each kind of target: points, minutes, or dollars. */
function amountSettings(rt: RewardTypeDTO | undefined, family: FamilyDTO) {
  if (!rt) return { step: 5, min: 1, suffix: family.currencyIcon, fallback: 10 };
  if (rt.kind === "time") return { step: 5, min: 1, suffix: "min", fallback: 15 };
  return { step: 0.5, min: 0.25, suffix: rt.unitLabel.length <= 2 ? rt.unitLabel : "$", fallback: 1 };
}

function PenaltyForm({ draft, setDraft, targets }: { draft: Draft; setDraft: (d: Draft) => void; targets: RewardTypeDTO[] }) {
  const { family } = useSession();
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const rt = targets.find((t) => t.id === draft.rewardTypeId);
  const amount = amountSettings(rt, family);
  const icons = PENALTY_ICONS.includes(draft.icon) ? PENALTY_ICONS : [draft.icon, ...PENALTY_ICONS];

  const changeTarget = (id: string) => {
    const next = targets.find((t) => t.id === id);
    const kindChanged = (next?.kind ?? "points") !== (rt?.kind ?? "points");
    set({ rewardTypeId: next?.id ?? null, amount: kindChanged ? amountSettings(next, family).fallback : draft.amount });
  };

  const amountHint = !rt
    ? `Taken from their ${family.currencyName} balance.`
    : rt.kind === "time"
      ? `Takes saved ${rt.name.toLowerCase()} first. If there isn't enough, the rest comes from ${family.currencyName} at your rate.`
      : `Converted to ${family.currencyName} at your rate.`;

  return (
    <div className="space-y-5">
      <TextField
        label="Name"
        placeholder="Not following instructions"
        value={draft.name}
        maxLength={60}
        onChange={(e) => set({ name: e.target.value })}
      />
      <div className="no-scrollbar -mx-1 -mt-3 flex gap-1.5 overflow-x-auto px-1 py-1">
        {PENALTY_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => set({ name: r })}
            className={cx(
              "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition",
              draft.name === r ? "bg-brand text-on-brand" : "bg-sunken text-muted ring-1 ring-line hover:text-ink",
            )}
          >
            {r}
          </button>
        ))}
      </div>
      <Field label="Icon">
        <IconPicker value={draft.icon} onChange={(icon) => set({ icon })} choices={icons} />
      </Field>
      <Field label="Take from">
        <Select value={draft.rewardTypeId ?? ""} onChange={(e) => changeTarget(e.target.value)}>
          <option value="">
            {family.currencyIcon} General balance ({family.currencyName})
          </option>
          {targets.map((t) => (
            <option key={t.id} value={t.id}>
              {t.icon} {t.name}
              {t.active ? "" : " (retired)"}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Amount" hint={amountHint}>
        {rt?.kind === "time" ? (
          <DurationInput value={draft.amount} onChange={(v) => set({ amount: v })} min={amount.min} max={24 * 60} step={amount.step} />
        ) : (
          <NumberStepper
            value={draft.amount}
            onChange={(v) => set({ amount: v })}
            min={amount.min}
            max={100_000}
            step={amount.step}
            suffix={amount.suffix}
          />
        )}
      </Field>
    </div>
  );
}

function PenaltyIdeasSheet({
  open,
  onClose,
  result,
  loading,
  onRetry,
  timeType,
}: {
  open: boolean;
  onClose: () => void;
  result: AdviceResponse<PenaltyIdeaDTO[]> | null;
  loading: boolean;
  onRetry: () => void;
  timeType: RewardTypeDTO | undefined;
}) {
  const { family } = useSession();
  const [added, setAdded] = useState<Set<PenaltyIdeaDTO>>(new Set());
  const add = useAction(
    (idea: PenaltyIdeaDTO) => {
      const toTime = idea.target === "time" && !!timeType;
      return api.post("/penalties", {
        name: idea.name,
        icon: idea.icon,
        amount: idea.amount,
        rewardTypeId: toTime ? timeType!.id : null,
      });
    },
    { success: "Preset added", onSuccess: (_r, idea) => setAdded((s) => new Set(s).add(idea)) },
  );

  const label = (idea: PenaltyIdeaDTO) =>
    idea.target === "time" && timeType
      ? `${formatDuration(idea.amount)} of ${timeType.name}`
      : `${idea.amount.toLocaleString()} ${currencyWord(idea.amount, family)}`;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Suggested penalties"
      size="lg"
      footer={
        result && !loading ? (
          <Button variant="secondary" block onClick={onRetry}>
            Ask again
          </Button>
        ) : undefined
      }
    >
      {loading || !result ? (
        <AdvisorThinking title="Sizing fair, recoverable consequences…" />
      ) : (
        <div className="space-y-3">
          <AdvisorSource source={result.source} note={result.note} />
          {result.items.length === 0 && <p className="py-6 text-center text-muted">No suggestions this time.</p>}
          {result.items.map((idea, i) => {
            const done = added.has(idea);
            return (
              <Card key={`${idea.name}-${i}`} className="p-3">
                <div className="flex items-start gap-3">
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-danger-soft text-2xl">{idea.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-extrabold">{idea.name}</div>
                    <div className="text-sm font-bold text-danger">−{label(idea)}</div>
                    <p className="mt-1 text-sm text-muted">{idea.rationale}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={done ? "soft" : "primary"}
                    onClick={() => add.mutate(idea)}
                    loading={add.isPending && add.variables === idea}
                    disabled={done || add.isPending}
                    className="shrink-0"
                  >
                    {done ? (
                      <>
                        <Check className="size-4" /> Added
                      </>
                    ) : (
                      <>
                        <Plus className="size-4" /> Add
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}

export function Discipline() {
  const { family } = useSession();
  const { data, isLoading } = usePenalties();
  const { data: rewardData } = useRewardTypes();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [ideas, setIdeas] = useState<AdviceResponse<PenaltyIdeaDTO[]> | null>(null);

  const save = useAction(
    (d: Draft) => {
      const { id, ...rest } = d;
      const body = { ...rest, name: rest.name.trim() };
      return id ? api.patch(`/penalties/${id}`, body) : api.post("/penalties", body);
    },
    { success: "Saved", onSuccess: () => setDraft(null) },
  );
  const remove = useAction((id: string) => api.del(`/penalties/${id}`), { success: "Deleted", onSuccess: () => setDraft(null) });
  const suggest = useAdvice(() => api.post<AdviceResponse<PenaltyIdeaDTO[]>>("/advisor/penalties"), setIdeas);

  if (isLoading || !data) return <PageLoader />;

  const types = rewardData?.rewardTypes ?? [];
  const activeTargets = types.filter((t) => t.active && isConvertible(t));
  // Keep a retired target selectable while editing a preset that still points at it.
  const targetsFor = (d: Draft) => {
    const current = types.find((t) => t.id === d.rewardTypeId);
    return current && !activeTargets.includes(current) ? [...activeTargets, current] : activeTargets;
  };
  const timeType = activeTargets.find((t) => t.kind === "time");
  const presets = data.presets;

  const openIdeas = () => {
    setIdeasOpen(true);
    if (!ideas && !suggest.isPending) suggest.mutate();
  };

  return (
    <div>
      <PageHeader title="Discipline" subtitle="Fair, predictable consequences" back="/parent/plan" />

      <div className="mt-2 rounded-3xl bg-brand-soft p-4">
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-card text-brand">
            <Scale className="size-5" />
          </span>
          <div className="min-w-0">
            <p className="font-extrabold text-brand-strong">
              Consequences work best when they're predictable, proportionate, and recoverable.
            </p>
            <ul className="mt-2 space-y-1 text-sm text-muted">
              <li>
                <b className="text-ink">Predictable</b> — kids know the cost ahead of time.
              </li>
              <li>
                <b className="text-ink">Proportionate</b> — a small slip costs a little.
              </li>
              <li>
                <b className="text-ink">Recoverable</b> — there's always a way back.
              </li>
            </ul>
          </div>
        </div>
      </div>

      <SectionTitle>Balance rules</SectionTitle>
      <BalanceRules
        key={`${family.allowNegative}:${family.negativeFloor}:${family.workItOffEnabled}`}
        family={family}
      />

      <SectionTitle
        action={
          <Button size="sm" variant="soft" onClick={() => setDraft({ ...empty })}>
            <Plus className="size-4" /> Add
          </Button>
        }
      >
        Penalty presets
      </SectionTitle>
      <p className="-mt-1 mb-3 px-1 text-sm text-muted">One-tap deductions you can use from a kid's page.</p>

      {presets.length === 0 ? (
        <EmptyState icon="⚖️" title="No presets yet" action={<Button onClick={() => setDraft({ ...empty })}>Add a preset</Button>}>
          Presets keep consequences consistent — the same slip always costs the same.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {presets.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer p-3 transition hover:ring-brand/40"
              onClick={() => setDraft({ id: p.id, name: p.name, icon: p.icon, amount: p.amount, rewardTypeId: p.rewardTypeId })}
            >
              <div className="flex items-center gap-3">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-danger-soft text-2xl">{p.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-extrabold">{p.name}</div>
                  <div className="truncate text-sm text-muted">
                    {p.rewardTypeId
                      ? `From saved ${types.find((t) => t.id === p.rewardTypeId)?.name ?? "rewards"}`
                      : "From general balance"}
                  </div>
                </div>
                {p.rewardTypeId ? (
                  <span className="shrink-0 rounded-full bg-danger-soft px-2.5 py-1 text-sm font-extrabold text-danger">
                    −{(() => {
                      const rt = types.find((t) => t.id === p.rewardTypeId);
                      return rt ? formatUnits(p.amount, rt) : p.amount;
                    })()}
                  </span>
                ) : (
                  <Points
                    value={-p.amount}
                    family={family}
                    className="shrink-0 rounded-full bg-danger-soft px-2.5 py-1 text-sm text-danger"
                  />
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-4">
        <AdvisorCard title="Suggest penalties" onClick={openIdeas}>
          ✨ Proportionate presets sized to what your kids can earn.
        </AdvisorCard>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-2xl bg-sunken px-4 py-3 text-sm text-muted">
        <Info className="mt-0.5 size-4 shrink-0" />
        <p>To apply a deduction, open a kid's page from Home. Nothing here changes a balance on its own.</p>
      </div>

      <Sheet
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? "Edit preset" : "New penalty preset"}
        footer={
          <div className="flex gap-2">
            {draft?.id && <ConfirmDelete onConfirm={() => draft.id && remove.mutate(draft.id)} loading={remove.isPending} />}
            <Button
              block
              onClick={() => draft && save.mutate(draft)}
              loading={save.isPending}
              disabled={!draft?.name.trim() || !(draft.amount > 0)}
            >
              Save
            </Button>
          </div>
        }
      >
        {draft && <PenaltyForm draft={draft} setDraft={setDraft} targets={targetsFor(draft)} />}
      </Sheet>

      <PenaltyIdeasSheet
        open={ideasOpen}
        onClose={() => setIdeasOpen(false)}
        result={ideas}
        loading={suggest.isPending}
        onRetry={() => suggest.mutate()}
        timeType={timeType}
      />
    </div>
  );
}
